import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { changePassword, clearToken, getToken, me, setToken } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';

const MIN_LENGTH = 12;
const inputClass = 'w-full border border-[#0a1f44]/20 px-3 py-3 text-base focus:outline-none focus:border-[#c8102e]';
const labelClass = 'block text-[10px] font-heading uppercase tracking-widest text-[#0a1f44]/60 mb-1';
const detailText = (detail) => (Array.isArray(detail) ? detail.map((e) => e.msg).join(' ') : detail);

export default function AdminAccount() {
  const { toast } = useToast();
  const [authOk, setAuthOk] = useState(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!getToken()) { setAuthOk(false); return; }
    me().then((data) => { setProfile(data); setAuthOk(true); }).catch(() => { clearToken(); setAuthOk(false); });
  }, []);

  const problems = [];
  if (form.next && form.next.length < MIN_LENGTH) problems.push(`At least ${MIN_LENGTH} characters`);
  if (form.next && form.current && form.next === form.current) problems.push('Must differ from your current password');
  if (form.confirm && form.next !== form.confirm) problems.push('Confirmation does not match');
  const canSubmit = form.current && form.next.length >= MIN_LENGTH && form.next === form.confirm && form.next !== form.current && !busy;

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError('');
    try {
      const data = await changePassword({ currentPassword: form.current, newPassword: form.next });
      setToken(data.token);
      setForm({ current: '', next: '', confirm: '' });
      setDone(true);
      toast({ title: 'Password updated', description: 'Other signed-in sessions were signed out.' });
    } catch (e) {
      setError(detailText(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (authOk === null) return <div className="min-h-screen bg-[#0a1f44] flex items-center justify-center text-white">Loading…</div>;
  if (authOk === false) return <Navigate to="/admin/login?next=/admin/account" replace />;

  return (
    <div className="min-h-screen bg-[#f5f6f8]" data-testid="admin-account-page">
      <header className="bg-[#0a1f44] text-white">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4"><img src={iceLogo} alt="ICE" className="h-10 w-auto" /><div><div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Staff Account</div><div className="font-display text-2xl leading-none mt-1">CHANGE PASSWORD</div></div></div>
          <Link to="/admin/announcements" data-testid="account-back-link" className="inline-flex items-center gap-1 text-white/70 hover:text-white font-heading uppercase tracking-widest text-[10px] px-3 py-2 border border-white/20"><ArrowLeft className="w-3 h-3" /> Admin</Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 py-10">
        <div className="bg-white border border-[#0a1f44]/10 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6"><div className="w-10 h-10 bg-[#c8102e] flex items-center justify-center text-white"><KeyRound className="w-5 h-5" /></div><div><div className="font-display text-[#0a1f44] text-xl">{profile?.name?.toUpperCase()}</div><div className="text-xs text-[#0a1f44]/60" data-testid="account-email">{profile?.email}</div></div></div>
          <p className="text-sm text-[#0a1f44]/70 mb-6">Replace your temporary password with one only you know. Use at least {MIN_LENGTH} characters. Any other devices signed in with your account will be signed out.</p>
          {done && <div className="mb-6 border-l-4 border-emerald-600 bg-emerald-50 p-4 flex items-start gap-3 text-sm text-emerald-900" data-testid="account-password-success"><ShieldCheck className="w-5 h-5 shrink-0" /> Your password was updated. You stay signed in on this device.</div>}
          <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
            <div><label className={labelClass}>Current Password</label><input type="password" autoComplete="current-password" required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} data-testid="account-current-password" className={inputClass} /></div>
            <div><label className={labelClass}>New Password</label><input type="password" autoComplete="new-password" required minLength={MIN_LENGTH} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} data-testid="account-new-password" className={inputClass} /></div>
            <div><label className={labelClass}>Confirm New Password</label><input type="password" autoComplete="new-password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="account-confirm-password" className={inputClass} /></div>
            {problems.length > 0 && <ul className="text-xs text-[#c8102e] space-y-1" data-testid="account-password-problems">{problems.map((p) => <li key={p}>• {p}</li>)}</ul>}
            {error && <div className="border-l-4 border-[#c8102e] bg-[#c8102e]/5 p-3 text-sm text-[#0a1f44]" data-testid="account-password-error">{error}</div>}
            <button type="submit" disabled={!canSubmit} data-testid="account-password-submit" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] disabled:opacity-50 text-white font-heading uppercase tracking-widest text-xs px-6 py-3 transition-colors">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update Password</button>
          </form>
        </div>
      </section>
    </div>
  );
}
